import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Award,
  Download,
  Lock,
  LogIn,
  ShieldCheck,
  Loader2,
  Eye,
} from 'lucide-react';
import type { Book } from '../../../shared/types/index.js';
import {
  downloadCertificatePdf,
  getCertificateBlobUrl,
  computeDigitalFingerprint,
} from '../utils/certificateGenerator.js';
import { useAuthStore } from '../../auth/store/useAuthStore.js';
import { api } from '../../../shared/api/api.js';
import { useBodyScrollLock } from '../../../shared/utils/scrollLock.js';
import { useNotificationStore } from '../../notifications/store/useNotificationStore.js';
import { CertificateActionHeader } from './certificate/CertificateActionHeader.js';
import { CertificatePdfPreview } from './certificate/CertificatePdfPreview.js';
import { CertificateCryptoTab } from './certificate/CertificateCryptoTab.js';
import { CertificateSummaryTab } from './certificate/CertificateSummaryTab.js';

interface CertificateModalProps {
  book: Book | null;
  isOpen: boolean;
  onClose: () => void;
}

export const CertificateModal: React.FC<CertificateModalProps> = ({
  book,
  isOpen,
  onClose,
}) => {
  const {
    user,
    openLoginModal,
    openRegisterModal,
  } = useAuthStore();

  const { showToast } = useNotificationStore();

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [fingerprint, setFingerprint] = useState<string>('');
  const [copiedFingerprint, setCopiedFingerprint] = useState(false);
  const [copiedSignature, setCopiedSignature] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [previewTab, setPreviewTab] = useState<'pdf' | 'crypto' | 'summary'>('pdf');
  const [authError, setAuthError] = useState<string | null>(null);

  // Cryptographic Signature & Blockchain Notarization states
  const [signatureData, setSignatureData] = useState<any>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState<any>(null);

  useBodyScrollLock(isOpen);

  useEffect(() => {
    let currentUrl: string | null = null;

    if (isOpen && book) {
      setAuthError(null);
      setIsGenerating(true);

      if (user) {
        // Authenticated user path
        api.getCertificate(book.id)
          .then((res: any) => {
            const sig = res?.certificate?.signatureData;
            setSignatureData(sig);
            computeDigitalFingerprint(book).then(setFingerprint);
            return getCertificateBlobUrl(book, sig);
          })
          .then((url) => {
            currentUrl = url;
            setBlobUrl(url);
          })
          .catch((err: any) => {
            console.error('Failed to generate or authenticate certificate:', err);
            setAuthError(err.message || 'Gagal memverifikasi hak akses sertifikat.');
          })
          .finally(() => {
            setIsGenerating(false);
          });
      } else {
        // Public user path (unauthenticated) - Generate public view-only certificate blob URL
        computeDigitalFingerprint(book).then(setFingerprint);
        getCertificateBlobUrl(book, null, true)
          .then((url) => {
            currentUrl = url;
            setBlobUrl(url);
          })
          .catch((err) => {
            console.error('Failed to generate public certificate preview:', err);
          })
          .finally(() => {
            setIsGenerating(false);
          });
      }
    } else {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
      setBlobUrl(null);
      setAuthError(null);
      setSignatureData(null);
      setVerificationResult(null);
    }

    return () => {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
    };
  }, [isOpen, book, user]);

  if (!isOpen || !book) return null;

  const handleDownload = async () => {
    if (!user) {
      showToast(
        'Akses Terbatas',
        'Pengguna publik hanya diperbolehkan melihat sertifikat saja. Silakan masuk untuk mengunduh.',
        'warning'
      );
      openLoginModal();
      return;
    }
    setIsDownloading(true);
    try {
      await downloadCertificatePdf(book, signatureData);
    } catch (err: any) {
      console.error('Download error:', err);
      setAuthError(err.message || 'Terjadi kesalahan saat mengunduh sertifikat.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleCopySignature = () => {
    if (!user) {
      showToast('Akses Terbatas', 'Pengguna publik tidak diperbolehkan menyalin tanda tangan digital.', 'warning');
      return;
    }
    if (!signatureData?.digitalSignature) return;
    navigator.clipboard.writeText(signatureData.digitalSignature);
    setCopiedSignature(true);
    setTimeout(() => setCopiedSignature(false), 2000);
  };

  const handleCopyFingerprint = () => {
    if (!user) {
      showToast('Akses Terbatas', 'Pengguna publik tidak diperbolehkan menyalin sidik jari digital.', 'warning');
      return;
    }
    if (!fingerprint) return;
    navigator.clipboard.writeText(fingerprint);
    setCopiedFingerprint(true);
    setTimeout(() => setCopiedFingerprint(false), 2000);
  };

  const handleVerifyCryptoSignature = async () => {
    if (!signatureData) return;
    setIsVerifying(true);
    setVerificationResult(null);
    try {
      const payload = {
        certNumber: `UIBN.REG/${new Date(book.createdAt).getFullYear()}/${book.uibn.replace(/[^0-9]/g, '')}`,
        uibn: book.uibn,
        bookId: book.id,
        title: book.title,
        userId: user?.id || '',
        issuedAt: signatureData.notarizedAt,
        signatureData,
      };
      const res = await api.verifyCertificateSignature(payload);
      setVerificationResult(res.verification);
    } catch (err: any) {
      console.error('Certificate verification error:', err?.message || err);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCopyVerificationLink = () => {
    if (!user) {
      showToast('Akses Terbatas', 'Pengguna publik tidak diperbolehkan menyalin tautan sertifikat.', 'warning');
      return;
    }
    const url = `${window.location.origin}/#search=${encodeURIComponent(book.uibn)}`;
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handlePrint = () => {
    if (!user) {
      showToast(
        'Akses Terbatas',
        'Pengguna publik hanya diperbolehkan melihat sertifikat saja. Silakan masuk untuk mencetak berkas.',
        'warning'
      );
      openLoginModal();
      return;
    }
    if (blobUrl) {
      const printWindow = window.open(blobUrl, '_blank');
      if (printWindow) {
        printWindow.focus();
      }
    } else {
      handleDownload();
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 bg-slate-950/70 backdrop-blur-xs animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        id="certificate-modal-container"
        className="bg-white w-full max-w-4xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[92vh]"
      >
        {/* Modal Header */}
        <div className="px-5 py-4 bg-slate-900 text-white flex items-center justify-between shrink-0 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-xl flex items-center justify-center shadow-md ${
                user
                  ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-slate-950'
                  : 'bg-slate-800 text-amber-400 border border-slate-700'
              }`}
            >
              <Award className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-bold text-base sm:text-lg text-white">
                  E-Sertifikat Digital Naskah Buku
                </h3>
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                  <ShieldCheck className="w-3 h-3" />
                  Sah & Terverifikasi
                </span>
              </div>
              <p className="text-xs text-slate-400 font-mono">
                UIBN: <strong className="text-amber-300">{book.uibn}</strong> • {book.title}
              </p>
            </div>
          </div>

          <button
            id="btn-close-certificate-modal"
            type="button"
            onClick={onClose}
            className="w-10 h-10 min-w-[44px] min-h-[44px] rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition cursor-pointer"
            title="Tutup Modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Action & Preview Switcher Bar */}
        <CertificateActionHeader
          user={user}
          previewTab={previewTab}
          onTabChange={setPreviewTab}
          copiedLink={copiedLink}
          onCopyVerificationLink={handleCopyVerificationLink}
          onPrint={handlePrint}
          onOpenLoginModal={openLoginModal}
        />

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          {previewTab === 'pdf' ? (
            <CertificatePdfPreview
              book={book}
              blobUrl={blobUrl}
              isGenerating={isGenerating}
              authError={authError}
              isPublic={!user}
              onClearAuthError={() => setAuthError(null)}
              onDownload={handleDownload}
            />
          ) : previewTab === 'crypto' ? (
            <CertificateCryptoTab
              signatureData={signatureData}
              copiedSignature={copiedSignature}
              isPublic={!user}
              onCopySignature={handleCopySignature}
              isVerifying={isVerifying}
              verificationResult={verificationResult}
              onVerifyCryptoSignature={handleVerifyCryptoSignature}
            />
          ) : (
            <CertificateSummaryTab
              book={book}
              user={user}
              fingerprint={fingerprint}
              copiedFingerprint={copiedFingerprint}
              isPublic={!user}
              onCopyFingerprint={handleCopyFingerprint}
            />
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-5 py-3 bg-slate-100 border-t border-slate-200 flex items-center justify-between shrink-0 text-xs text-slate-500">
          <div className="flex items-center gap-1.5">
            {user ? (
              <>
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                <span>Tervalidasi Digital • Standar Format A4 Landscape</span>
              </>
            ) : (
              <>
                <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="font-semibold text-slate-700">Tervalidasi Resmi Database Katalog UIBN Nasional</span>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4.5 py-2 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 hover:text-slate-900 font-semibold shadow-2xs transition-all duration-150 cursor-pointer min-h-[44px] active:scale-[0.98] flex items-center justify-center"
            >
              Tutup
            </button>
            {user && (
              <button
                type="button"
                id="btn-download-pdf-cert"
                onClick={handleDownload}
                disabled={isDownloading}
                className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-bold flex items-center justify-center gap-2 shadow-sm hover:shadow-md transition-all duration-150 cursor-pointer disabled:opacity-50 min-h-[44px] active:scale-[0.98]"
              >
                {isDownloading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Menerbitkan PDF...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    <span>Unduh Sertifikat PDF</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
